import { useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { Button, vars } from "@pack-it-up/ui-web";
import type { CalendarDate } from "@pack-it-up/shared";
import { createInlineEdit } from "@pack-it-up/viewmodel";
import type { Trip, TripPatch } from "@pack-it-up/core";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { ClockIcon13 } from "../../app/icons.js";
import { fmtDateRange } from "../../lib/format.js";
import { EditAffordance, ListTopbar, editableFieldStyle } from "../shared/ListTopbar.js";
import { dateInput as themedDateInput, editableField, editableHover } from "../shared/ListTopbar.css.js";

// Kit `.dates-popover`: start/end date inputs, each clearable, Cancel/Save.
const DatesPopover = ({
  start,
  end,
  onSave,
  onClose,
}: {
  start?: CalendarDate;
  end?: CalendarDate;
  onSave: (start: string | null, end: string | null) => void;
  onClose: () => void;
}): ReactNode => {
  const [draftStart, setDraftStart] = useState<string>(start ?? "");
  const [draftEnd, setDraftEnd] = useState<string>(end ?? "");
  const rowHead: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between" };
  const rowLabel: CSSProperties = {
    fontSize: "10px",
    color: vars.color.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    fontWeight: 600,
  };
  const clearBtn: CSSProperties = {
    fontSize: "10px",
    color: vars.color.textMuted,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    fontFamily: "inherit",
    textDecoration: "underline",
    textUnderlineOffset: "2px",
  };
  const dateInput: CSSProperties = {
    background: vars.color.background,
    border: `1px solid ${vars.color.border}`,
    borderRadius: vars.radius.sm,
    padding: "6px 8px",
    color: vars.color.text,
    fontFamily: "inherit",
    fontSize: vars.font.sizeXs,
    outline: "none",
  };
  return (
    <div
      role="dialog"
      aria-label="Edit trip dates"
      style={{
        position: "absolute",
        top: "26px",
        left: 0,
        zIndex: 30,
        background: vars.color.surfaceRaised,
        border: `1px solid ${vars.color.border}`,
        borderRadius: vars.radius.lg,
        padding: vars.space.md,
        display: "flex",
        flexDirection: "column",
        gap: vars.space.sm,
        width: "220px",
        boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <div style={rowHead}>
          <label htmlFor="dp-start" style={rowLabel}>
            Start date
          </label>
          <button type="button" style={clearBtn} onClick={() => setDraftStart("")}>
            Clear
          </button>
        </div>
        <input id="dp-start" type="date" className={themedDateInput} style={dateInput} value={draftStart} onChange={(e) => setDraftStart(e.target.value)} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
        <div style={rowHead}>
          <label htmlFor="dp-end" style={rowLabel}>
            End date
          </label>
          <button type="button" style={clearBtn} onClick={() => setDraftEnd("")}>
            Clear
          </button>
        </div>
        <input id="dp-end" type="date" className={themedDateInput} style={dateInput} value={draftEnd} onChange={(e) => setDraftEnd(e.target.value)} />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            onSave(draftStart === "" ? null : draftStart, draftEnd === "" ? null : draftEnd);
            onClose();
          }}
        >
          Save
        </Button>
      </div>
    </div>
  );
};

// The dates half of the topbar meta: the clock + range button, plus the popover editor
// it anchors. A sibling of `LocationField` — the two are the trip's meta slot, and the
// popover has to be positioned relative to the button that opens it, so they travel
// together rather than as loose JSX in the topbar's `meta` prop.
const DatesField = ({
  start,
  end,
  canEdit,
  open,
  onOpenChange,
  onSave,
}: {
  start?: CalendarDate;
  end?: CalendarDate;
  canEdit: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (start: string | null, end: string | null) => void;
}): ReactNode => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", position: "relative" }}>
    {ClockIcon13}
    <button
      type="button"
      disabled={!canEdit}
      aria-label="Trip dates"
      className={canEdit ? [editableField, editableHover].join(" ") : editableField}
      // Undated trips read as a placeholder, italic like "Add location", not as a value.
      style={{ ...editableFieldStyle, cursor: canEdit ? "pointer" : "default", fontStyle: start === undefined ? "italic" : "normal" }}
      onClick={() => onOpenChange(!open)}
    >
      {fmtDateRange(start, end)}
    </button>
    {open && canEdit && (
      <DatesPopover
        {...(start !== undefined ? { start } : {})}
        {...(end !== undefined ? { end } : {})}
        onSave={onSave}
        onClose={() => onOpenChange(false)}
      />
    )}
  </span>
);

// Inline-editable location in the topbar meta ("Add location" affordance when unset;
// empty commit clears it via `location: null`).
const LocationField = ({ location, canEdit, onSave }: { location?: string; canEdit: boolean; onSave: (location: string | null) => void }): ReactNode => {
  const edit = useMemo(() => createInlineEdit(), []);
  const mode = useStoreValue(edit, (s) => s.mode);
  const draft = useStoreValue(edit, (s) => s.draft);

  const commit = (): void => {
    // Unlike names, an emptied location is a deliberate clear, so commit the raw draft.
    edit.getState().cancel();
    const trimmed = draft.trim();
    if (trimmed !== (location ?? "")) onSave(trimmed === "" ? null : trimmed);
  };

  if (location === undefined && !canEdit) return null;
  if (canEdit && mode === "editing") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "2px" }}>
        <input
          aria-label="Trip location"
          autoFocus
          value={draft}
          onChange={(e) => edit.getState().setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") edit.getState().cancel();
          }}
          style={{
            ...editableFieldStyle,
            cursor: "text", // an actual text input, unlike the resting field it replaces
            background: vars.color.surfaceRaised,
            outline: `1px solid ${vars.color.accent}`,
            width: "140px",
          }}
        />
        <EditAffordance onSave={commit} onCancel={() => edit.getState().cancel()} />
      </span>
    );
  }
  return (
    <button
      type="button"
      disabled={!canEdit}
      className={canEdit ? [editableField, editableHover].join(" ") : editableField}
      style={{ ...editableFieldStyle, cursor: canEdit ? "pointer" : "default" }}
      onClick={() => edit.getState().beginEdit("trip-location", location ?? "")}
    >
      {location ?? <span style={{ fontStyle: "italic" }}>Add location</span>}
    </button>
  );
};

// Kit `.topbar` for the trip detail view: editable title, dates (popover editor),
// location, all wired to `updateTrip`. 56px strip under the app-shell edge.
export const TripTopbar = ({
  trip,
  canEdit,
  isOwner,
  isViewer,
  onUpdateTrip,
  onArchive,
  onDelete,
  onSaveAsTemplate,
  onCopyTrip,
  actions,
  datesOpen: datesOpenProp,
  onDatesOpenChange,
}: {
  trip: Trip;
  canEdit: boolean;
  /** Archive/delete are owner-only — they end the trip for every other participant. */
  isOwner: boolean;
  isViewer: boolean;
  onUpdateTrip: (patch: Omit<TripPatch, "tripId">) => void;
  /** Doc 10 §5: archive is reversible, so this fires straight from the "⋮" menu — no confirm dialog. */
  onArchive?: () => void;
  /** Permanent delete — opens the confirm dialog (parent-owned), same menu as Archive. */
  onDelete?: () => void;
  /** Issue #8 — "Save as template": the trip's structure becomes a new template. */
  onSaveAsTemplate?: () => void;
  /** Issue #8 — "Copy trip": structure only, into a new trip you own. */
  onCopyTrip?: () => void;
  /** Right-aligned topbar actions (e.g. Share). */
  actions?: ReactNode;
  /**
   * Dates-popover open state, controlled by the parent (`TripDetailPage`) so the
   * countdown meta-chip can also trigger the same popover anchored here. Falls back to
   * internal state when omitted, so `TripTopbar` stays usable on its own.
   */
  datesOpen?: boolean;
  onDatesOpenChange?: (open: boolean) => void;
}): ReactNode => {
  const [localDatesOpen, setLocalDatesOpen] = useState(false);
  const datesOpen = datesOpenProp ?? localDatesOpen;
  const setDatesOpen = onDatesOpenChange ?? setLocalDatesOpen;
  // Copying reads the trip and writes a *new* list, so a viewer may do it (issue #8).
  // Archive and delete decide the trip's life for everyone on it, so both are the
  // owner's alone — an editor edits contents (the rules agree: `archived` sits behind
  // `ownerOnlyFieldsUnchanged`, delete behind `isOwnerDoc`).
  const menuItems = [
    ...(onSaveAsTemplate !== undefined ? [{ label: "Save as template", onClick: onSaveAsTemplate }] : []),
    ...(onCopyTrip !== undefined ? [{ label: "Copy trip", onClick: onCopyTrip }] : []),
    ...(isOwner && onArchive !== undefined ? [{ label: "Archive trip", onClick: onArchive }] : []),
    ...(isOwner && onDelete !== undefined ? [{ label: "Delete trip", onClick: onDelete, danger: true }] : []),
  ];
  return (
    <ListTopbar
      name={trip.name}
      nameLabel="Trip name"
      canEdit={canEdit}
      isViewer={isViewer}
      onRename={(name) => onUpdateTrip({ name })}
      menuLabel="More options for this trip"
      menuItems={menuItems}
      {...(actions !== undefined ? { actions } : {})}
      meta={
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px", color: vars.color.textMuted, fontSize: vars.font.sizeSm }}>
          <DatesField
            {...(trip.startDate !== undefined ? { start: trip.startDate } : {})}
            {...(trip.endDate !== undefined ? { end: trip.endDate } : {})}
            canEdit={canEdit}
            open={datesOpen}
            onOpenChange={setDatesOpen}
            onSave={(startDate, endDate) => onUpdateTrip({ startDate, endDate })}
          />
          <span aria-hidden style={{ color: vars.color.border }}>
            ·
          </span>
          <LocationField
            {...(trip.location !== undefined ? { location: trip.location } : {})}
            canEdit={canEdit}
            onSave={(location) => onUpdateTrip({ location })}
          />
        </div>
      }
    />
  );
};
